import { buildQuestions, seedAreas, seedTargets } from './data';
import {
  altShortKey,
  loadAltShortLabels,
  loadOptionShortLabels,
  optionShortKey,
} from './lib/short-label-store';
import {
  DEFAULT_CLOSING_PROMPT,
  DEFAULT_OPENING_PROMPT,
  DEFAULT_TARGET_TEMPLATE,
} from './templates';
import type {
  Area,
  BaseImage,
  ChoiceQuestion,
  CropArea,
  ExpertQuestionConfig,
  PhotoQuestion,
  Question,
  Target,
} from './types';

export interface AppState {
  questions: Question[];
  openingPrompt: string;
  closingPrompt: string;
  targetTemplate: string;
  targets: Target[];
  areas: Area[];
  cropArea: CropArea | null;
  baseImage: BaseImage | null;
  questionsEditMode: boolean;
  lockedQuestionIds: Set<string>;
  expertConfig: Map<string, ExpertQuestionConfig>;
  expertPermutationMode: 'permute' | 'cover';
  expertAltCoverageMode: 'random' | 'simple' | 'smart';
  expertIterationsPerPermutation: number;
  expertConcurrency: number;
  expertReviewPerArea: boolean;
}

const STOPWORDS = new Set(['my', 'a', 'an', 'the', 'of', 'in', 'on', 'for', 'with', 'and', 'or']);

const SHORT_LABEL_OVERRIDES: Record<string, string> = {
  'dress codes': 'dress',
  'stuffy fine-dining restaurants': 'dine',
  'velvet ropes': 'rope',
  'vip lines': 'vip',
  'corporate gatekeeping': 'corp',
  'my dog': 'dog',
  'my cat': 'cat',
  'my bird': 'bird',
  'my laptop': 'laptop',
  'my phone': 'phone',
  'something wilder!': 'wild',
  'personal milestone': 'milestone',
  'quality time with friends': 'friends',
  'music': 'music',
  'food & drinks': 'food',
  'art & creativity': 'art',
  'sports': 'sports',
};

export function slugifyShortLabel(label: string): string {
  const key = label.trim().toLowerCase();
  if (SHORT_LABEL_OVERRIDES[key]) return SHORT_LABEL_OVERRIDES[key];
  const tokens = key
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s-]+/)
    .filter(Boolean);
  const first = tokens.find((t) => !STOPWORDS.has(t)) ?? tokens[0] ?? 'opt';
  return first.slice(0, 16);
}


export function buildDefaultExpertQuestionConfig(q: Question): ExpertQuestionConfig {
  const options =
    q.kind === 'choice'
      ? q.options.map((o) => ({ inPool: true, shortLabel: slugifyShortLabel(o.label) }))
      : [];
  return {
    questionId: q.id,
    mode: 'fixed',
    options,
    photoMode: 'fixed',
    altMode: q.randomMode,
  };
}

export function buildDefaultExpertConfig(
  questions: Question[],
): Map<string, ExpertQuestionConfig> {
  const map = new Map<string, ExpertQuestionConfig>();
  for (const q of questions) {
    map.set(q.id, buildDefaultExpertQuestionConfig(q));
  }
  return map;
}

const initialTargets = seedTargets();
const initialQuestions = buildQuestions();
applySavedShortLabels(initialQuestions);

function applySavedShortLabels(questions: Question[]): void {
  const altOverrides = loadAltShortLabels();
  for (const q of questions) {
    if (q.kind === 'choice') {
      q.options.forEach((opt, optIdx) => {
        opt.prompts.forEach((group, promptIdx) => {
          group.alternatives.forEach((alt, altIdx) => {
            const v = altOverrides[altShortKey(q.id, optIdx, promptIdx, altIdx)];
            if (v !== undefined) alt.shortLabel = v;
          });
        });
      });
    } else {
      q.prompts.forEach((group, promptIdx) => {
        group.alternatives.forEach((alt, altIdx) => {
          const v = altOverrides[altShortKey(q.id, 0, promptIdx, altIdx)];
          if (v !== undefined) alt.shortLabel = v;
        });
      });
    }
  }
}

function applySavedOptionShortLabels(cfg: Map<string, ExpertQuestionConfig>): void {
  const overrides = loadOptionShortLabels();
  for (const [qid, q] of cfg) {
    q.options.forEach((o, i) => {
      const v = overrides[optionShortKey(qid, i)];
      if (v !== undefined) o.shortLabel = v;
    });
  }
}

export const state: AppState = {
  questions: initialQuestions,
  openingPrompt: DEFAULT_OPENING_PROMPT,
  closingPrompt: DEFAULT_CLOSING_PROMPT,
  targetTemplate: DEFAULT_TARGET_TEMPLATE,
  targets: initialTargets,
  areas: seedAreas(initialTargets),
  cropArea: null,
  baseImage: null,
  questionsEditMode: false,
  lockedQuestionIds: new Set(),
  expertConfig: (() => {
    const cfg = buildDefaultExpertConfig(initialQuestions);
    applySavedOptionShortLabels(cfg);
    return cfg;
  })(),
  expertPermutationMode: 'permute',
  expertAltCoverageMode: 'random',
  expertIterationsPerPermutation: 1,
  expertConcurrency: 3,
  expertReviewPerArea: true,
};

export function ensureExpertConfigShape(): void {
  for (const q of state.questions) {
    const existing = state.expertConfig.get(q.id);
    if (!existing) {
      state.expertConfig.set(q.id, buildDefaultExpertQuestionConfig(q));
      continue;
    }
    if (q.kind === 'choice') {
      const nextOptions = q.options.map((o, i) => {
        const prev = existing.options[i];
        return prev
          ? { inPool: prev.inPool, shortLabel: prev.shortLabel || slugifyShortLabel(o.label) }
          : { inPool: true, shortLabel: slugifyShortLabel(o.label) };
      });
      existing.options = nextOptions;
    } else {
      existing.options = [];
    }
  }
  for (const qid of Array.from(state.expertConfig.keys())) {
    if (!state.questions.some((q) => q.id === qid)) state.expertConfig.delete(qid);
  }
}

type Listener = () => void;
const contentListeners = new Set<Listener>();
const structureListeners = new Set<Listener>();

export function subscribeContent(listener: Listener): () => void {
  contentListeners.add(listener);
  return () => contentListeners.delete(listener);
}

export function subscribeStructure(listener: Listener): () => void {
  structureListeners.add(listener);
  return () => structureListeners.delete(listener);
}

export function notifyContent(): void {
  for (const listener of contentListeners) listener();
}

export function notifyStructure(): void {
  for (const listener of structureListeners) listener();
  for (const listener of contentListeners) listener();
}

export function isChoice(q: Question): q is ChoiceQuestion {
  return q.kind === 'choice';
}

export function isPhoto(q: Question): q is PhotoQuestion {
  return q.kind === 'photo';
}
